/**
 * OR-6230: read a concept group's unified ``assetStatus`` for the inline chat
 * card, keyed on the **real catalog id** (minted at save, OR-6289) — which the
 * card carries in ``hintArgs.conceptGroupId``, NOT its ``resourceId`` (that's the
 * ``cgpreview.<previewId>`` routing/slot key the canonical endpoint can't resolve).
 * Hits the canonical per-CG detail endpoint ``GET /concept-groups/{id}`` — which
 * resolves a published catalog CG *or*, on a miss, falls back to the in-flight
 * DynamoDB draft — and reads the backend-derived ``assetStatus``, so the status is
 * derived by **fetch location**: Snowflake → Ready, draft-fallback → Draft.
 *
 * This reads the **canonical asset**, mirroring how the cohort card reads the
 * cohort profile and the AV card reads ``/by-ref``. No poll: a CG has no
 * async-worker step; the Draft → Ready flip is a user publish action, caught by
 * the ``useConfirmPreview`` invalidation (keyed on the same real id).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { authFetch } from './authFetch';

const CONCEPT_GROUPS_PATH = '/concept-groups';

// Only the fields the card needs; the endpoint returns the full
// ConceptGroupResponseSchema. ``assetStatus`` is a tolerant string at the wire
// (mirrors cohort/AV) so a future backend value renders rather than soft-failing.
const conceptGroupStatusSchema = z.object({
  conceptGroupId: z.string().nullish(),
  conceptGroupName: z.string().nullish(),
  assetStatus: z.string().nullish(),
});

const conceptGroupStatusEnvelopeSchema = z.object({ data: conceptGroupStatusSchema });

export type ConceptGroupStatusDetail = z.infer<typeof conceptGroupStatusSchema>;

export const conceptGroupStatusKeys = {
  detail: (conceptGroupId: string) => ['concept-group-status', conceptGroupId] as const,
};

/**
 * Read a concept group's ``assetStatus`` by its stable catalog id (the card's
 * ``resourceId``). Disabled until an id is known.
 *
 * ``defer`` (OR-6858): also disables the query even once an id is known —
 * set by a sibling ``useBatchPrewarmConceptGroups`` while its own batch
 * request is in flight, so this card doesn't fire its own request in the
 * same render pass the batch does. ``staleTime`` matters here specifically
 * because of that batching: without it, the moment ``defer`` flips back to
 * false, TanStack treats the just-seeded cache entry as instantly stale and
 * fires a background refetch anyway — silently defeating the whole point of
 * seeding it. An explicit ``useConfirmPreview``/rename invalidation still
 * forces a refetch regardless of this.
 */
export const useConceptGroupStatusByRef = (
  conceptGroupId: string | null | undefined,
  projectId: string | undefined,
  defer = false
): UseQueryResult<ConceptGroupStatusDetail> =>
  useQuery({
    queryKey: conceptGroupStatusKeys.detail(conceptGroupId ?? ''),
    enabled: Boolean(conceptGroupId) && !defer,
    staleTime: 60_000,
    queryFn: async (): Promise<ConceptGroupStatusDetail> => {
      const qs = new URLSearchParams();
      if (projectId) qs.set('projectId', projectId);
      const qsStr = qs.toString() ? `?${qs}` : '';
      const json = await authFetch<unknown>(
        `${CONCEPT_GROUPS_PATH}/${encodeURIComponent(conceptGroupId ?? '')}${qsStr}`
      );
      return conceptGroupStatusEnvelopeSchema.parse(json).data;
    },
  });
